import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toSmartTitleCase } from '../utils/toSmartTitleCase';
import { el } from 'date-fns/locale';

import { api } from '../config'

const FiltersContext = createContext();

export const useFilters = () => {
	const context = useContext(FiltersContext);
	if (!context) {
		throw new Error('useFilters must be used within a FiltersProvider');
	}
	return context;
};

export const FiltersProvider = ({ children }) => {
	const location = useLocation();
	const [establishments, setEstablishments] = useState([]);
	const [isLoadingEstablishments, setIsLoadingEstablishments] = useState(false);
	const [showFilters, setShowFilters] = useState(true);

	const [establishmentExternal, setEstablishmentExternal] = useState([]);
	const [companyExternal, setCompanyExternal] = useState([]);
	const [areaExternal, setAreaExternal] = useState([]);
	const [neighbourhoodExternal, setNeighbourhoodExternal] = useState([]);
	const [regionExternal, setRegionExternal] = useState([]);
	
	const [selectedRegions, setSelectedRegions] = useState([]);
	const [selectedBarrios, setSelectedBarrios] = useState([]);
	const [selectedCentros, setSelectedCentros] = useState([]);
	const [selectedEmpresas, setSelectedEmpresas] = useState([]);
	const [selectedAreas, setSelectedAreas] = useState([]);


	useEffect(() => {
		setSelectedRegions([]);
		setSelectedBarrios([]);
		setSelectedCentros([]);
		setSelectedEmpresas([]);
		setSelectedAreas([]);
	}, [location.pathname]);


	const filterOptions = useMemo(() => {
		if (!establishments.length) return { region: [], barrio: [], centro: [], empresa: [], area: [] };

		const getOptions = (key, getValue) => {
			const optionsSet = new Set();
			establishments.forEach(est => {
				const estRegion = est.region_centro || (typeof est.region === 'string' ? est.region : est.region?.name);
				const estBarrio = est.comuna_centro || est.comuna || est.ciudad;
				const estCentroCode = est.siep_code || est.code_siep || `${est.latitud}_${est.longitud}` || `${est.latitude}_${est.longitude}`;
				const empresa = est.empresa;
				const area = est.area;

				const regionOk = key === 'region' || !selectedRegions.length || selectedRegions.includes(estRegion);
				const barrioOk = key === 'barrio' || !selectedBarrios.length || selectedBarrios.includes(estBarrio);
				const centroOk = key === 'centro' || !selectedCentros.length || (() => {
					const centroName = toSmartTitleCase(est.nombre_centro || est.planta || est.titulares || est.name || '');
					return selectedCentros.includes(centroName);
				})();
				const empresaOk = key === 'empresa' || !selectedEmpresas.length || selectedEmpresas.includes(empresa);
				const areaOk = key === 'area' || !selectedAreas.length || selectedAreas.includes(area);


				let shouldInclude = false;
				
				if (key === 'region') {
					shouldInclude = barrioOk && centroOk && empresaOk && areaOk;
				} else if (key === 'barrio') {
					shouldInclude = regionOk && centroOk && empresaOk && areaOk;
				} else if (key === 'centro') {
					shouldInclude = regionOk && barrioOk && empresaOk && areaOk;
				} else if (key === 'empresa') {
					shouldInclude = regionOk && barrioOk && centroOk && areaOk;
				} else if (key === 'area') {
					shouldInclude = regionOk && barrioOk && centroOk && empresaOk;
				}

				if (shouldInclude) {
					const value = getValue(est);
					if (value) optionsSet.add(JSON.stringify(value)); 
				}
			});
			return Array.from(optionsSet).map(v => JSON.parse(v));
		};

		let regionOptions = [];
		if(!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){
			// regionOptions = regionExternal.map(region => ({ value: region.nombre, label: toSmartTitleCase(region.nombre) }));
			regionOptions = [
				...new Map(
					establishmentExternal.map(est => [
					est?.nombreRegion || '',
					{ value: est?.nombreRegion || '', label: toSmartTitleCase(est?.nombreRegion || 'Vacío') }
					])
				).values()
			];
		}else{
			regionOptions = getOptions('region', est => est.region_centro || (typeof est.region === 'string' ? est.region : est.region?.name))
			.map(region => ({ value: region, label: toSmartTitleCase(region) }));
		}
		
		let barrioOptions = [];
		if(!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){
			console.log('establishment', establishmentExternal)
			// barrioOptions = neighbourhoodExternal.map(neighbourhood => ({ value: neighbourhood.nombre, label: toSmartTitleCase(neighbourhood.nombre) }));
			barrioOptions = [
				...new Map(
					establishmentExternal.map(est => [
					est?.nombreBarrio || '',
					{ value: est?.nombreBarrio || '', label: toSmartTitleCase(est?.nombreBarrio || 'Vacío') }
					])
				).values()
			];
		}else{
			barrioOptions = getOptions('barrio', est => est.comuna_centro || est.comuna || est.ciudad)
			.map(barrio => ({ value: barrio, label: toSmartTitleCase(barrio) }));
		}
		
		let centroOptions = [];
		if(establishmentExternal?.length){
			centroOptions = (establishmentExternal?.length ? establishmentExternal.map(establishment => ({ value: establishment.nombre, label: toSmartTitleCase(establishment.nombre) })) : []);
		}else{
			const centroMap = new Map();
			getOptions('centro', est => {
				const centroName = est.nombre_centro || est.planta || est.titulares || est.name;
				if (!centroName || centroName.toLowerCase().includes('undefined')) return null;
				return toSmartTitleCase(centroName);
			})
			.filter(name => name && name !== '' && name.toLowerCase() !== 'undefined')
			.forEach(name => {
				centroMap.set(name, { value: name, label: name });
			});
			centroOptions = Array.from(centroMap.values());
		}

		let empresaOptions = [];
		if(!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){
			// empresaOptions = companyExternal.map(empresa => ({ value: empresa.nombreTitular, label: toSmartTitleCase(empresa.nombreTitular) }));
			empresaOptions = [
				...new Map(
					establishmentExternal.map(est => [
					est?.nombreTitular || '',
					{ value: est?.nombreTitular || '', label: toSmartTitleCase(est?.nombreTitular || 'Vacío') }
					])
				).values()
			];
		}else{
			empresaOptions = getOptions('empresa', est => est.empresa)
			.map(empresa => ({ value: empresa, label: toSmartTitleCase(empresa) }));
		}

		let areaOptions = [];
		if(!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){
			// areaOptions = areaExternal.map(area => ({ value: area.nombre, label: toSmartTitleCase(area.nombre) }));
			areaOptions = [
				...new Map(
					establishmentExternal.map(est => [
					est?.nombreArea || '',
					{ value: est?.nombreArea || '', label: toSmartTitleCase(est?.nombreArea || 'Vacío') }
					])
				).values()
			];
		}else{
			areaOptions = getOptions('area', est => est.area)
			.map(area => ({ value: area, label: toSmartTitleCase(area) }));
		}		

		return {
			region: regionOptions,
			barrio: barrioOptions,
			centro: centroOptions,
			empresa: empresaOptions,
			area: areaOptions,
		};
	}, [
		establishments,
		selectedRegions,
		selectedBarrios,
		selectedCentros,
		selectedEmpresas,
		selectedAreas
	]);

	const filteredEstablishments = useMemo(() => {
		const establishmentList = (!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL) ? establishmentExternal : establishments); 
		
		return establishmentList.filter(est => {
			const estRegion = est.region_centro || (typeof est.region === 'string' ? est.region : est.region?.name) || est.nombreRegion;
			const regionOk = !selectedRegions.length || selectedRegions.includes(estRegion);

			const estBarrio = est.comuna_centro || est.comuna || est.ciudad || est.nombreBarrio;
			const barrioOk = !selectedBarrios.length || selectedBarrios.includes(estBarrio);

			const centroName = toSmartTitleCase(est.nombre_centro || est.planta || est.titulares || est.name || est.nombre ||'');
			const centroOk = !selectedCentros?.length || selectedCentros.includes(centroName);

			const empresaName = toSmartTitleCase(est.empresa || est.nombreTitular || '');
			const empresaOk = !selectedEmpresas.length || selectedEmpresas.includes(empresaName);
			
			const areaName = toSmartTitleCase(est.area || est.nombreArea || '');
			const areaOk = !selectedAreas.length || selectedAreas.includes(areaName);

			if (!centroName || centroName.toLowerCase().includes('undefined')) return false;

			return regionOk && barrioOk && centroOk && empresaOk && areaOk;
		});
	}, [establishments, establishmentExternal, selectedRegions, selectedBarrios, selectedCentros, selectedEmpresas, selectedAreas]);

	const mapEstablishments = useMemo(() => {
		return (filteredEstablishments?.length ? (
			filteredEstablishments
				.filter((establishment, index) => { // Filter establishment by latitude/longitude allow (-90 - 90)
					if (!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){ // Only resource map is data SPED original
						const latitud = establishment.latitud || establishment.latitude || null;
						const longitud = establishment.longitud || establishment.longitude || null;

						let latitudOk = false;
						let longitudOk = false;
						if(
							latitud && 
							latitud >= -90 && 
							latitud <=90
						) latitudOk = true;
						if(
							longitud && 
							longitud >= -90 && 
							longitud <=90
						) longitudOk = true;

						return (
							latitudOk && 
							longitudOk ? 
								true : false
						);
					}else{
						return true;
					}
				})
				.map((establishment, index) => {
					let mappedEstablishment = {};

					if (!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){ // Only establishment external isn't empty
						
						mappedEstablishment = {
							id: establishment.pk,
							latitude: establishment.latitud || establishment.latitude || null,
							longitude: establishment.longitud || establishment.longitude || null,
							name: toSmartTitleCase(String(establishment.nombre || '')),
							code_siep: String(establishment.codigoSERNAPESCA),
							state: establishment.state || 'green',
							figurePoint: 'circle_point',
							originalEstablishment: establishment,
							showHexagons: undefined,
						};
					}else{
						// Si el objeto ya trae showHexagons explícito, respetar ese valor
						const showHexagonsValue =
							typeof establishment.showHexagons === 'boolean'
								? establishment.showHexagons
								: undefined;

						// Use data external sped
						mappedEstablishment = {
								id: establishment._id || establishment.siep_code || establishment.code_siep || `${establishment.latitud}_${establishment.longitud}`,
								latitude: establishment.latitud || establishment.latitude || null,
								longitude: establishment.longitud || establishment.longitude || null,
								name: toSmartTitleCase(String(establishment.nombre_centro || '')),
								code_siep: String(establishment.siep_code || establishment.code_siep || `${establishment.latitud}_${establishment.longitud}`),
								state: establishment.state || 'green',
								figurePoint: 'circle_point',
								originalEstablishment: establishment,
								showHexagons: showHexagonsValue !== undefined ? showHexagonsValue : true,
							};

						if (establishment.region_centro && establishment.nombre_centro) {
							mappedEstablishment = {
								id: establishment._id || establishment.siep_code || establishment.code_siep || `${establishment.latitud}_${establishment.longitud}`,
								latitude: establishment.latitud || establishment.latitude || null,
								longitude: establishment.longitud || establishment.longitude || null,
								name: toSmartTitleCase(String(establishment.nombre_centro || '')),
								code_siep: String(establishment.siep_code || establishment.code_siep || `${establishment.latitud}_${establishment.longitud}`),
								state: establishment.state || 'green',
								figurePoint: 'circle_point',
								originalEstablishment: establishment,
								showHexagons: showHexagonsValue !== undefined ? showHexagonsValue : true,
							};
						}
						else if (establishment.planta) {
							mappedEstablishment = {
								id: establishment._id || establishment.siep_code || `${establishment.latitud}_${establishment.longitud}`,
								latitude: establishment.latitud || null,
								longitude: establishment.longitud || null,
								name: toSmartTitleCase(String(establishment.planta || '')),
								code_siep: String(establishment.siep_code || `${establishment.latitud}_${establishment.longitud}`),
								state: establishment.state || 'green',
								figurePoint: 'diamond_point',
								originalEstablishment: establishment,
								showHexagons: showHexagonsValue !== undefined ? showHexagonsValue : true,
							};
						}
						else if (establishment.titulares || establishment.name) {
							mappedEstablishment = {
								id: establishment.siep_code || establishment.code_siep || establishment._id || `${establishment.latitude}_${establishment.longitude}`,
								latitude: establishment.latitude || null,
								longitude: establishment.longitude || null,
								name: toSmartTitleCase(String(establishment.titulares || establishment.name || '')),
								code_siep: String(establishment.siep_code || establishment.code_siep || `${establishment.latitude}_${establishment.longitude}`),
								state: establishment.state || 'green',
								figurePoint: establishment.titulares ? 'diamond_point' : 'circle_point',
								originalEstablishment: establishment,
								showHexagons: showHexagonsValue !== undefined ? showHexagonsValue : (establishment.name ? true : false),
							};
						}
						else {
							mappedEstablishment = {
								id: establishment._id || establishment.siep_code || establishment.code_siep || `${establishment.latitud || establishment.latitude}_${establishment.longitud || establishment.longitude}`,
								latitude: establishment.latitud || establishment.latitude || null,
								longitude: establishment.longitud || establishment.longitude || null,
								name: toSmartTitleCase(String(establishment.nombre_centro || establishment.planta || establishment.titulares || establishment.name || 'Sin nombre')),
								code_siep: String(establishment.siep_code || establishment.code_siep || `${establishment.latitud || establishment.latitude}_${establishment.longitud || establishment.longitude}`),
								state: establishment.state || 'green',
								figurePoint: 'circle_point',
								originalEstablishment: establishment,
								showHexagons: showHexagonsValue !== undefined ? showHexagonsValue : true,
							};
						}
					}
					return mappedEstablishment;
				})
		) : []);
	}, [filteredEstablishments, establishmentExternal]);

	const clearAllFilters = () => {
		setSelectedRegions([]);
		setSelectedBarrios([]);
		setSelectedCentros([]);
		setSelectedEmpresas([]);
		setSelectedAreas([]);
	};

	const hasActiveFilters = selectedRegions.length > 0 || selectedBarrios.length > 0 || 
		selectedCentros.length > 0 || selectedEmpresas.length > 0 || selectedAreas.length > 0;

	const value = {
		// Data
		establishments,
		setEstablishments,
		establishmentExternal,
		setEstablishmentExternal,
		companyExternal,
		setCompanyExternal,
		areaExternal,
		setAreaExternal,
		neighbourhoodExternal,
		setNeighbourhoodExternal,
		regionExternal,
		setRegionExternal,
		filteredEstablishments,
		mapEstablishments,
		isLoadingEstablishments,
		setIsLoadingEstablishments,
		
		// Filter options
		filterOptions,
		
		// Filter state
		showFilters,
		setShowFilters,
		selectedRegions,
		setSelectedRegions,
		selectedBarrios,
		setSelectedBarrios,
		selectedCentros,
		setSelectedCentros,
		selectedEmpresas,
		setSelectedEmpresas,
		selectedAreas,
		setSelectedAreas,
		
		// Helper functions
		clearAllFilters,
		hasActiveFilters,
	};

	return (
		<FiltersContext.Provider value={value}>
			{children}
		</FiltersContext.Provider>
	);
};

export default FiltersContext;
